/**
 * Honesty by construction (design-language#honesty-by-construction).
 *
 * A number on a screen makes a claim, and the claim is not only "42" — it is
 * "42, right now, from somewhere that answered". Today every surface renders
 * the first half and drops the second, so a query that failed and a queue that
 * is genuinely empty both render `0`, and the more reassuring reading wins.
 *
 * This module makes the second half part of the VALUE rather than a convention
 * each screen re-remembers. `Provenance<T>` cannot be constructed without
 * saying where the value came from and when it was true, and `UNAVAILABLE`
 * carries no value at all — so "could not determine" is a shape the type system
 * distinguishes from a real answer, not a `0` with a comment beside it.
 *
 * Two places in this repo already got this right by hand and are the pattern
 * rather than exceptions: the email reconcile sweep reports `UNAVAILABLE` with
 * no counts, rather than `{ scanned: 0 }`, and the integrations screen renders
 * an em dash rather than `0` when its fetch failed. Both had to be argued for
 * individually. The point of a type is that the next one does not.
 */

/**
 * How much weight a value can bear.
 *
 * Ordered from most to least load-bearing, and the order is the point: a
 * surface may freely present something more trustworthy than it promised, and
 * never something less.
 */
export type ProvenanceKind =
  /** Read from the system of record on this request. */
  | 'live'
  /** Derived by comparing two sources that agreed, or converged after a sweep. */
  | 'reconciled'
  /** Stated by a person or an external system; true because someone said so. */
  | 'asserted'
  /** Computed forward from live data. Renders with a `~` prefix. */
  | 'projected'
  /** Sample or placeholder. Renders hatched and muted, and says so. */
  | 'illustrative';

/** A value that answered, with the receipts. */
export interface Known<T> {
  readonly status: 'known';
  readonly value: T;
  readonly kind: ProvenanceKind;
  /** Where it came from, in words a person reading a screen would accept. */
  readonly source: string;
  /** ISO 8601. When the value was last true, NOT when it was rendered. */
  readonly asOf: string;
}

/**
 * A value that did not answer.
 *
 * It carries NO value field, deliberately. An `Unavailable` with a `value: 0`
 * on it would be read as zero by the first caller in a hurry, which is the
 * exact failure this module exists to make impossible.
 */
export interface Unavailable {
  readonly status: 'unavailable';
  /** Why, in words that belong on a screen rather than in a log. */
  readonly reason: string;
  /** What was attempted, so a reader knows which system to go and look at. */
  readonly source: string;
}

export type Provenance<T> = Known<T> | Unavailable;

export function known<T>(
  value: T,
  meta: { kind: ProvenanceKind; source: string; asOf: string },
): Known<T> {
  return { status: 'known', value, ...meta };
}

export function unavailable(meta: { reason: string; source: string }): Unavailable {
  return { status: 'unavailable', ...meta };
}

export function isKnown<T>(p: Provenance<T>): p is Known<T> {
  return p.status === 'known';
}

/**
 * The value, or a fallback — for the places that genuinely have one.
 *
 * Deliberately NOT named `valueOr(p, 0)`, and deliberately awkward to reach
 * for: passing a zero here is how "could not determine" becomes "nothing
 * exists" one call site at a time. Use it for a fallback that is TRUE when
 * unknown (an empty list to iterate, a disabled flag), never for a count.
 */
export function valueOrFallback<T>(p: Provenance<T>, fallback: T): T {
  return isKnown(p) ? p.value : fallback;
}

/** Map the value, carrying the provenance through untouched. */
export function mapProvenance<T, U>(p: Provenance<T>, fn: (value: T) => U): Provenance<U> {
  return isKnown(p) ? { ...p, value: fn(p.value) } : p;
}

/**
 * Combine several provenances into one over a tuple.
 *
 * Unavailable is ABSORBING: if any input could not answer, the result cannot
 * either. A dashboard tile computed from three queries where one failed is not
 * two-thirds right, it is wrong — and the kind degrades to the least
 * load-bearing of the inputs, because a figure is only as live as its stalest
 * ingredient.
 */
export function allProvenance<T extends readonly unknown[]>(parts: {
  [K in keyof T]: Provenance<T[K]>;
}): Provenance<T> {
  const list = parts as readonly Provenance<unknown>[];
  const firstMissing = list.find((p): p is Unavailable => !isKnown(p));
  if (firstMissing) return firstMissing;

  const knowns = list as readonly Known<unknown>[];
  return {
    status: 'known',
    value: knowns.map((p) => p.value) as unknown as T,
    kind: weakestKind(knowns.map((p) => p.kind)),
    source: [...new Set(knowns.map((p) => p.source))].join(', '),
    // The OLDEST `asOf`, not the newest: the combined figure stopped being
    // true when its first ingredient did.
    asOf: knowns.map((p) => p.asOf).sort()[0] ?? new Date(0).toISOString(),
  };
}

const KIND_ORDER: ProvenanceKind[] = ['live', 'reconciled', 'asserted', 'projected', 'illustrative'];

function weakestKind(kinds: readonly ProvenanceKind[]): ProvenanceKind {
  return kinds.reduce(
    (worst, kind) => (KIND_ORDER.indexOf(kind) > KIND_ORDER.indexOf(worst) ? kind : worst),
    'live' as ProvenanceKind,
  );
}
